import React from "react";
import { FaFileDownload, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileArchive, FaFile, FaCalendar, FaWeight } from "react-icons/fa";
import { media_url } from "../../hooks/useBlog";
import { logEvent } from "../../services/analytics";

const BlogAttachments = ({ files }) => {
  if (!files || files.length === 0) {
    return null;
  }

  // Filtrar solo archivos activos
  const activeFiles = files.filter(file => file.is_active);

  if (activeFiles.length === 0) {
    return null;
  }

  // Función para obtener el icono según el tipo de archivo
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    const iconMap = {
      pdf: <FaFilePdf className="file-icon pdf" />,
      doc: <FaFileWord className="file-icon word" />,
      docx: <FaFileWord className="file-icon word" />,
      xls: <FaFileExcel className="file-icon excel" />,
      xlsx: <FaFileExcel className="file-icon excel" />,
      ppt: <FaFilePowerpoint className="file-icon powerpoint" />,
      pptx: <FaFilePowerpoint className="file-icon powerpoint" />,
      jpg: <FaFileImage className="file-icon image" />,
      jpeg: <FaFileImage className="file-icon image" />,
      png: <FaFileImage className="file-icon image" />,
      gif: <FaFileImage className="file-icon image" />,
      zip: <FaFileArchive className="file-icon archive" />,
      rar: <FaFileArchive className="file-icon archive" />,
      '7z': <FaFileArchive className="file-icon archive" />,
    };

    return iconMap[extension] || <FaFile className="file-icon default" />;
  };

  // Función para formatear el tamaño del archivo
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Tamaño desconocido';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  // Manejar la descarga del archivo
  const handleDownload = (file) => {
    // Registrar evento en Google Analytics
    logEvent('Blog', 'Download File', file.name, file.id);
    
    // Abrir el archivo en una nueva pestaña
    window.open(media_url(file.file), '_blank');
  };

  return (
    <div className="blog-attachments-section">
      <div className="attachments-header">
        <h4 className="attachments-title">
          <FaFileDownload /> Documentos Adjuntos
        </h4>
        <span className="attachments-count">{activeFiles.length} {activeFiles.length === 1 ? 'archivo' : 'archivos'}</span>
      </div>

      <div className="attachments-grid">
        {activeFiles.map((file) => (
          <div 
            key={file.id} 
            className="attachment-card"
            onClick={() => handleDownload(file)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleDownload(file);
            }}
          >
            <div className="attachment-icon-wrapper">
              {getFileIcon(file.name)}
            </div>
            
            <div className="attachment-content">
              <h5 className="attachment-name">{file.name}</h5>
              
              {file.caption && (
                <p className="attachment-caption">{file.caption}</p>
              )}
              
              <div className="attachment-meta">
                {file.size && (
                  <span className="meta-item">
                    <FaWeight className="meta-icon" />
                    {file.size}
                  </span>
                )}
                
                {(file.published_at || file.created) && (
                  <span className="meta-item">
                    <FaCalendar className="meta-icon" />
                    {formatDate(file.published_at || file.created)}
                  </span>
                )}
              </div>
            </div>

            <div className="attachment-download-btn">
              <FaFileDownload />
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .blog-attachments-section {
          margin-top: 3rem;
          margin-bottom: 2rem;
          padding: 2rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          border: 1px solid #dee2e6;
        }

        .attachments-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #dee2e6;
        }

        .attachments-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #212529;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .attachments-count {
          background: #007bff;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .attachments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.25rem;
        }

        .attachment-card {
          background: white;
          border-radius: 10px;
          padding: 1.25rem;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          cursor: pointer;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
        }

        .attachment-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #007bff 0%, #0056b3 100%);
          transform: scaleY(0);
          transition: transform 0.3s ease;
        }

        .attachment-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 123, 255, 0.15);
          border-color: #007bff;
        }

        .attachment-card:hover::before {
          transform: scaleY(1);
        }

        .attachment-icon-wrapper {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .attachment-card:hover .attachment-icon-wrapper {
          background: #e7f3ff;
          transform: scale(1.1);
        }

        .file-icon {
          font-size: 1.75rem;
        }

        .file-icon.pdf { color: #dc3545; }
        .file-icon.word { color: #2b579a; }
        .file-icon.excel { color: #217346; }
        .file-icon.powerpoint { color: #d24726; }
        .file-icon.image { color: #6f42c1; }
        .file-icon.archive { color: #fd7e14; }
        .file-icon.default { color: #6c757d; }

        .attachment-content {
          flex: 1;
          min-width: 0;
        }

        .attachment-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: #212529;
          margin: 0 0 0.5rem 0;
          word-break: break-word;
          line-height: 1.4;
        }

        .attachment-caption {
          font-size: 0.875rem;
          color: #6c757d;
          margin: 0 0 0.75rem 0;
          line-height: 1.4;
        }

        .attachment-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.8rem;
          color: #868e96;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }

        .meta-icon {
          font-size: 0.75rem;
        }

        .attachment-download-btn {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #007bff;
          color: white;
          border-radius: 50%;
          font-size: 1rem;
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.3s ease;
        }

        .attachment-card:hover .attachment-download-btn {
          opacity: 1;
          transform: scale(1);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .blog-attachments-section {
            padding: 1.5rem;
            margin-top: 2rem;
          }

          .attachments-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .attachments-title {
            font-size: 1.25rem;
          }

          .attachments-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .attachment-card {
            padding: 1rem;
          }

          .attachment-download-btn {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 480px) {
          .attachment-icon-wrapper {
            width: 40px;
            height: 40px;
          }

          .file-icon {
            font-size: 1.5rem;
          }

          .attachment-name {
            font-size: 0.9rem;
          }

          .attachment-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogAttachments;
